"use client";

import { Box } from "@mui/material";
import { PLPFilterProductClass, PLPSearchParams } from "../../products/(plp)/types";
import { useBrandCategoryNavigation } from "./brandCategoryNavigation";

export default function BrandCategoryChips({
    categories,
    brandSlug,
    searchParams,
}: {
    categories: PLPFilterProductClass[];
    brandSlug: string;
    searchParams: PLPSearchParams;
}) {
    const { isActive, go, onKeyDown } = useBrandCategoryNavigation(brandSlug, searchParams);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                // On a phone the row stays on one line and scrolls rather than stacking.
                // From lg the column has a fixed width with space beside it, where a cut
                // off chip reads as a bug rather than as something to scroll, so it wraps.
                flexWrap: { xs: "nowrap", lg: "wrap" },
                overflowX: { xs: "auto", lg: "visible" },
                overscrollBehaviorX: "contain",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                // The row reaches past the page gutter so a chip can scroll all the
                // way off a phone screen, then pads itself back to line up with the
                // text above. The padding also keeps the last chip's border clear of
                // the clipping edge, which otherwise eats it at the end of the scroll.
                mx: { xs: -2, sm2: -3, lg: -0.5 },
                px: { xs: 2, sm2: 3, lg: 0.5 },
                py: 0.5,
            }}
        >
            {categories.map((category) => {
                const active = isActive(category.slug);

                return (
                    <Box
                        key={category.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={active}
                        onClick={() => go(category)}
                        onKeyDown={onKeyDown(category)}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            minHeight: 48,
                            bgcolor: active ? "grey.900" : "#fff",
                            color: active ? "#fff" : "grey.900",
                            border: "1px solid",
                            borderColor: active ? "grey.900" : "grey.300",
                            borderRadius: 1,
                            px: { xs: 2, sm2: 2.5 },
                            py: 1.25,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                            transition: "150ms",
                            "&:hover": {
                                bgcolor: active ? "grey.800" : "grey.50",
                                borderColor: "grey.900",
                            },
                        }}
                        onDragStart={(event) => event.preventDefault()}
                    >
                        {category.site_name}
                    </Box>
                );
            })}
        </Box>
    );
}
